import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronUp, FaQuestionCircle } from 'react-icons/fa';
import StripeNavbar from './StripeNavbar';

export default function FAQ() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => {
    const cookieLang = document.cookie.match(/taealam_language=([^;]+)/);
    return cookieLang ? cookieLang[1] : 'en';
  });
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const isArabic = lang === 'ar';
  const isMobile = window.innerWidth <= 768;

  const faqs = {
    en: [
      {
        id: 1,
        question: 'How do I find a teacher?',
        answer: 'You can browse our network of qualified teachers by subject, price, or rating. Use the search filters to find the perfect teacher for your needs. Once you find a teacher you like, you can view their profile and book a session.'
      },
      {
        id: 2,
        question: 'How do I book a session?',
        answer: 'After signing up and logging in, browse available teachers and select one that matches your needs. Click on their profile to see their availability, then choose a time slot that works for you. Complete the booking by confirming your session details.'
      },
      {
        id: 3,
        question: 'What subjects can I learn?',
        answer: 'Taealam offers a wide range of subjects including Mathematics, Science, English, Arabic, History, and many more. You can search for teachers by subject to find the perfect match for your learning goals.'
      },
      {
        id: 4,
        question: 'How much does a session cost?',
        answer: 'Session prices vary depending on the teacher and subject. Each teacher sets their own hourly rate, which is displayed on their profile. You can filter teachers by price range to find options that fit your budget.'
      },
      {
        id: 5,
        question: 'Can I cancel or reschedule a session?',
        answer: 'Yes, you can cancel or reschedule sessions through your dashboard. Please check the cancellation policy for each teacher, as policies may vary. We recommend canceling at least 24 hours in advance when possible.'
      },
      {
        id: 6,
        question: 'How do I communicate with my teacher?',
        answer: 'Taealam has a built-in messaging system that allows you to chat directly with your teacher. You can send messages, ask questions, and coordinate session details all within the platform.'
      },
      {
        id: 7,
        question: 'Are sessions recorded?',
        answer: 'Yes, all sessions are automatically recorded and transcribed. You can access recordings and transcripts from your dashboard to review the material at any time.'
      },
      {
        id: 8,
        question: 'How do I rate a teacher?',
        answer: 'After completing a session, you\'ll be prompted to rate your teacher. You can provide a star rating and write a review about your experience. Your feedback helps other students make informed decisions.'
      },
      {
        id: 9,
        question: 'What payment methods are accepted?',
        answer: 'Taealam accepts various payment methods including credit cards and debit cards. All payments are processed securely through our payment system.'
      },
      {
        id: 10,
        question: 'How do I get help if I have a problem?',
        answer: 'You can visit our Help Center for detailed guides and answers to common questions. If you need additional assistance, you can contact our support team through the Help Center.'
      }
    ],
    ar: [
      {
        id: 1,
        question: 'كيف أجد معلمًا؟',
        answer: 'يمكنك تصفح شبكتنا من المعلمين المؤهلين حسب المادة أو السعر أو التقييم. استخدم فلاتر البحث للعثور على المعلم المثالي لاحتياجاتك. بمجرد العثور على معلم يعجبك، يمكنك عرض ملفه الشخصي وحجز جلسة.'
      },
      {
        id: 2,
        question: 'كيف أحجز جلسة؟',
        answer: 'بعد التسجيل وتسجيل الدخول، تصفح المعلمين المتاحين واختر من يناسب احتياجاتك. انقر على ملفه الشخصي لرؤية توافره، ثم اختر وقتًا يناسبك. أكمل الحجز بتأكيد تفاصيل جلستك.'
      },
      {
        id: 3,
        question: 'ما هي المواد التي يمكنني تعلمها؟',
        answer: 'تعلّم يقدم مجموعة واسعة من المواد بما في ذلك الرياضيات والعلوم والإنجليزية والعربية والتاريخ والعديد غيرها. يمكنك البحث عن المعلمين حسب المادة للعثور على المطابقة المثالية لأهدافك التعليمية.'
      },
      {
        id: 4,
        question: 'كم تكلفة الجلسة؟',
        answer: 'تختلف أسعار الجلسات حسب المعلم والمادة. يحدد كل معلم سعره بالساعة، والذي يظهر على ملفه الشخصي. يمكنك تصفية المعلمين حسب النطاق السعري للعثور على خيارات تناسب ميزانيتك.'
      },
      {
        id: 5,
        question: 'هل يمكنني إلغاء أو إعادة جدولة جلسة؟',
        answer: 'نعم، يمكنك إلغاء أو إعادة جدولة الجلسات من خلال لوحة التحكم الخاصة بك. يرجى التحقق من سياسة الإلغاء لكل معلم، حيث قد تختلف السياسات. نوصي بالإلغاء قبل 24 ساعة على الأقل عند الإمكان.'
      },
      {
        id: 6,
        question: 'كيف أتواصل مع معلمي؟',
        answer: 'تعلّم لديه نظام مراسلة مدمج يتيح لك الدردشة مباشرة مع معلمك. يمكنك إرسال الرسائل وطرح الأسئلة وتنسيق تفاصيل الجلسة كل ذلك داخل المنصة.'
      },
      {
        id: 7,
        question: 'هل يتم تسجيل الجلسات؟',
        answer: 'نعم، يتم تسجيل جميع الجلسات ونسخها تلقائيًا. يمكنك الوصول إلى التسجيلات والنصوص من لوحة التحكم الخاصة بك لمراجعة المادة في أي وقت.'
      },
      {
        id: 8,
        question: 'كيف أقيم معلمًا؟',
        answer: 'بعد إكمال جلسة، سيُطلب منك تقييم معلمك. يمكنك تقديم تقييم بالنجوم وكتابة مراجعة عن تجربتك. تساعد ملاحظاتك الطلاب الآخرين على اتخاذ قرارات مستنيرة.'
      },
      {
        id: 9,
        question: 'ما هي طرق الدفع المقبولة؟',
        answer: 'يقبل تعلّم طرق دفع مختلفة بما في ذلك بطاقات الائتمان والخصم. تتم معالجة جميع المدفوعات بأمان من خلال نظام الدفع الخاص بنا.'
      },
      {
        id: 10,
        question: 'كيف أحصل على المساعدة إذا واجهت مشكلة؟',
        answer: 'يمكنك زيارة مركز المساعدة للحصول على أدلة مفصلة وإجابات على الأسئلة الشائعة. إذا كنت بحاجة إلى مساعدة إضافية، يمكنك الاتصال بفريق الدعم من خلال مركز المساعدة.'
      }
    ]
  };

  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div dir={isArabic ? 'rtl' : 'ltr'} style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <StripeNavbar lang={lang} setLang={setLang} alwaysScrolled={true} />
      
      <div style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: isMobile ? '100px 20px 60px' : '120px 40px 80px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
            marginBottom: 24,
            color: 'white'
          }}>
            <FaQuestionCircle size={32} />
          </div>
          <h1 style={{
            fontSize: isMobile ? 32 : 42,
            fontWeight: 800,
            color: '#0F172A',
            marginBottom: 12,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
          </h1>
          <p style={{
            fontSize: 18,
            color: '#64748B',
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.6,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic 
              ? 'ابحث عن إجابات لأسئلتك الشائعة حول منصة تعلّم'
              : 'Find answers to common questions about Taealam platform'}
          </p>
        </div>

        {/* FAQ List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {faqs[lang].map((faq) => (
            <div
              key={faq.id}
              style={{
                background: '#FFFFFF',
                borderRadius: 16,
                border: '1px solid #E2E8F0',
                overflow: 'hidden',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                style={{
                  width: '100%',
                  padding: '20px 24px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 16
                }}
              >
                <h3 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#0F172A',
                  margin: 0,
                  flex: 1,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  ...(isArabic ? { textAlign: 'right' } : {})
                }}>
                  {faq.question}
                </h3>
                <div style={{
                  color: '#FF7426',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {expandedFaqs[faq.id] ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                </div>
              </button>
              
              {expandedFaqs[faq.id] && (
                <div style={{
                  padding: '0 24px 20px 24px',
                  borderTop: '1px solid #E2E8F0'
                }}>
                  <p style={{
                    fontSize: 16,
                    color: '#64748B',
                    lineHeight: 1.7,
                    margin: '16px 0 0 0',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                  }}>
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Help Section */}
        <div style={{
          marginTop: 60,
          padding: '40px',
          background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
          borderRadius: 20,
          textAlign: 'center',
          color: 'white'
        }}>
          <h2 style={{
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 12,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'لا تزال لديك أسئلة؟' : 'Still have questions?'}
          </h2>
          <p style={{
            fontSize: 16,
            marginBottom: 24,
            opacity: 0.95,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic 
              ? 'قم بزيارة مركز المساعدة للحصول على المزيد من المعلومات'
              : 'Visit our Help Center for more information'}
          </p>
          <button
            onClick={() => navigate('/help-center')}
            style={{
              background: 'white',
              color: '#FF7426',
              border: 'none',
              padding: '12px 32px',
              borderRadius: 50,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'IBM Plex Sans Arabic', sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {isArabic ? 'زيارة مركز المساعدة' : 'Visit Help Center'}
          </button>
        </div>
      </div>
    </div>
  );
}

